import React from 'react';
import { Typography, Link, Box, Grid } from '@mui/material';
import Icon from '@mui/material/Icon';
import { useIntl } from 'react-intl';
import ButtonInput from '../inputs/ButtonInput';
import Copyright from '../copyright/Copyright';
import { useInputs } from '@bellawatt/use-inputs';
import { useDepletedBatterySites } from '../../utils/useDepletedBatterySites';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAnalytics } from '../../utils/useAnalytics';
import styled from '@emotion/styled';

const FooterBox = styled(Box)`
  padding: 25px 0;
  background-color: ${(props) => props.theme.palette.primary.main};
`;

const LogoBox = styled(Box)`
  margin-bottom: ${(props) => props.theme.spacing(4)};
`;

const SocialIconsBox = styled(Box)`
  margin-bottom: ${(props) => props.theme.spacing(3)};
`;

const LinksBox = styled(Box)`
  margin-bottom: ${(props) => props.theme.spacing(3)};
  ${(props) => props.theme.breakpoints.down('sm')} {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
`;

const TextLink = styled(Link)`
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  position: relative;
  margin-right: 20px;
  ${(props) => props.theme.breakpoints.up('sm')} {
    &:not(:last-child):after {
      color: grey;
      position: absolute;
      top: 0;
      right: -10px;
      content: '|';
    }
  }
`;

const CopyrightText = styled(Typography)`
  color: #fff;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
`;

const Hr = styled.hr`
  border: 1px solid #d9d9d9;
`;

const BottomButtonContainer = styled(Box)`
  padding: 40px 20px;
  justify-content: center;
  display: flex;
`;

const BottomButtons = styled(Grid)`
  width: 100%;
  max-width: 1135px;
  gap: 8px;
  ${(props) => props.theme.breakpoints.down('sm')} {
    width: 100%;
  }
`;

const Btn = styled(ButtonInput)`
  color: ${(props) => props.theme.palette.common.accentBlue};
  border-color: ${(props) => props.theme.palette.common.accentBlue};
  text-transform: none;
  border-radius: 0;
  min-width: 320px;
  ${(props) => props.theme.breakpoints.down('sm')} {
    width: 100%;
  }
`;

const SocialIcon = ({ href, imgSrc, imgClass, altText, onClick }) => (
  <a href={href} target="_blank" rel="noreferrer" aria-label={altText} onClick={onClick}>
    <Icon>
      <img className={imgClass} src={imgSrc} alt={altText} />
    </Icon>
  </a>
);

export default function Footer({ hideRebates }) {
  const { GaTracker } = useAnalytics();
  const { formatMessage } = useIntl();
  const { isOnboarded } = useInputs();
  const { depletedBatterySites } = useDepletedBatterySites();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('sm'));

  const path = window.location.pathname;
  const showPrint =
    isOnboarded && isLargeScreen && path === '/fleets' && depletedBatterySites.length === 0;

  const handleLinkClick = (url, name, blank = false) => {
    GaTracker.trackEvent({
      category: 'DTE Fleets Link',
      action: 'Clicked DTE Footer Link',
      label: `Clicked on ${name}`,
    });

    if (blank) {
      return window.open(url, '_blank');
    }

    return window.open(url);
  };

  return (
    <Box role="contentinfo">
      {!hideRebates && (
        <>
          <Hr />
          <BottomButtonContainer className="container">
            <BottomButtons container justifyContent="space-between">
              <Btn
                variant="outlined"
                href="#"
                onClick={() =>
                  handleLinkClick(
                    formatMessage({ id: 'footer.footerButtons.chargerRebates.url' }),
                    formatMessage({ id: 'footer.footerButtons.chargerRebates.text' }),
                    true,
                  )
                }
              >
                {formatMessage({ id: 'footer.footerButtons.chargerRebates.text' })}
              </Btn>
              <Btn
                variant="outlined"
                href="#"
                onClick={() =>
                  handleLinkClick(
                    formatMessage({ id: 'footer.footerButtons.advisoryServices.url' }),
                    formatMessage({ id: 'footer.footerButtons.advisoryServices.text' }),
                    true,
                  )
                }
              >
                {formatMessage({ id: 'footer.footerButtons.advisoryServices.text' })}
              </Btn>
              {showPrint && (
                <Btn
                  variant="outlined"
                  href="#"
                  onClick={() =>
                    handleLinkClick(
                      formatMessage({ id: 'footer.footerButtons.printResults.url' }),
                      formatMessage({ id: 'footer.footerButtons.printResults.text' }),
                      true,
                    )
                  }
                >
                  {formatMessage({ id: 'footer.footerButtons.printResults.text' })}
                </Btn>
              )}
            </BottomButtons>
          </BottomButtonContainer>
        </>
      )}
      <FooterBox>
        <LogoBox>
          <img src={theme.footer.logoPath} alt="DTE" />
        </LogoBox>
        <SocialIconsBox>
          <SocialIcon
            href="#"
            onClick={() =>
              handleLinkClick(
                formatMessage({ id: 'footer.footerButtons.twitter.url' }),
                'Twitter',
                true,
              )
            }
            imgSrc={theme.footer.socialIconPaths.twitter}
            imgClass="imageIcon"
            altText="Twitter"
          />
          <SocialIcon
            href="#"
            onClick={() =>
              handleLinkClick(
                formatMessage({ id: 'footer.footerButtons.facebook.url' }),
                'Facebook',
                true,
              )
            }
            imgSrc={theme.footer.socialIconPaths.facebook}
            imgClass="imageIcon"
            altText="Facebook"
          />
          <SocialIcon
            href="#"
            onClick={() =>
              handleLinkClick(
                formatMessage({ id: 'footer.footerButtons.linkedin.url' }),
                'LinkedIn',
                true,
              )
            }
            imgSrc={theme.footer.socialIconPaths.linkedIn}
            imgClass="imageIcon"
            altText="LinkedIn"
          />
          <SocialIcon
            href="#"
            onClick={() =>
              handleLinkClick(
                formatMessage({ id: 'footer.footerButtons.youtube.url' }),
                'YouTube',
                true,
              )
            }
            imgSrc={theme.footer.socialIconPaths.youTube}
            imgClass="imageIcon"
            altText="YouTube"
          />
        </SocialIconsBox>
        <LinksBox>
          <TextLink
            href="#"
            onClick={() =>
              handleLinkClick(
                formatMessage({ id: 'footer.termsAndConditions.url' }),
                formatMessage({ id: 'footer.termsAndConditions.text' }),
                true,
              )
            }
          >
            {formatMessage({ id: 'footer.termsAndConditions.text' })}
          </TextLink>
          <TextLink
            href="#"
            onClick={() =>
              handleLinkClick(
                formatMessage({ id: 'footer.privacyPolicy.url' }),
                formatMessage({ id: 'footer.privacyPolicy.text' }),
                true,
              )
            }
          >
            {formatMessage({ id: 'footer.privacyPolicy.text' })}
          </TextLink>
          <TextLink
            href="#"
            onClick={() =>
              handleLinkClick(
                formatMessage({ id: 'footer.contactUs.url' }),
                formatMessage({ id: 'footer.contactUs.text' }),
                true,
              )
            }
          >
            {formatMessage({ id: 'footer.contactUs.text' })}
          </TextLink>
        </LinksBox>
        <CopyrightText>
          <Copyright />
        </CopyrightText>
      </FooterBox>
    </Box>
  );
}
